import { generateId } from '@ai-sdk/provider-utils';
import { atom, type WritableAtom } from 'nanostores';
import { renderProblems } from './prompt';
import { registerShellHandler } from './shell-handler';
import { trackAttemptFix, trackMessageErrors, trackProblem, trackStripeIntegrationErrors } from './tracking';
import { filterVitePreTransformDupes, fuzzyCompare, stringifyProblem } from './util';
import { clientFetch } from '~/lib/.client/fetch';
import { isCurrentChatUsingBoltAgent } from '~/lib/ai';
import { chatHooks } from '~/lib/chat-hooks';
import { workbenchStore } from '~/lib/stores/workbench';
import type { ErrorReasoning, Message, PotentialProblem, PotentialProblemsMessage } from '~/types/chat';
import { fileMapToImportFiles } from '~/utils/import-files';
import { logger } from '~/utils/logger';
import { createSnapshot } from '~/utils/snapshots';

class ErrorHandler {
  id = generateId();
  currentProblems: WritableAtom<PotentialProblem[]> = atom([]);

  init() {
    registerShellHandler();
  }

  async push(argProblem: Omit<PotentialProblem, 'id' | 'count'> | PotentialProblem) {
    const currentProblems = this.currentProblems.get();

    const problem = {
      id: 'id' in argProblem ? argProblem.id : generateId(),
      count: 'count' in argProblem ? argProblem.count : 1,
      ...argProblem,
    } as PotentialProblem;

    const problemStr = stringifyProblem(problem).trim();

    if (problemStr.length === 0) {
      logger.warn(`Captured error was ignored ${JSON.stringify(problem)}`);

      return;
    }

    // check if error already exists
    const existingIndex = currentProblems.findIndex((problem) => fuzzyCompare(stringifyProblem(problem), problemStr));

    if (existingIndex !== -1) {
      const existing = currentProblems[existingIndex];

      // add to count
      currentProblems.splice(existingIndex, 1, { ...problem, count: existing.count + 1 });

      this.currentProblems.set([...currentProblems]);

      return;
    }

    // if there are already more than 10 unique problems, ignore future ones
    if (currentProblems.length > 10) {
      return;
    }

    // we have a new problem
    const problems = filterVitePreTransformDupes([...currentProblems, problem]);

    this.currentProblems.set(problems);

    trackProblem(problem);

    trackStripeIntegrationErrors(await this.getChatMessages(), currentProblems);
  }

  async resolve(userContext?: string) {
    const message = this.getMessage(userContext);

    if (!(await chatHooks.checkUserAcknowledgeLargeContext(message))) {
      return;
    }

    await workbenchStore.saveAllFiles();

    const messages = await this.getChatMessages();

    createSnapshot(messages);

    if (isCurrentChatUsingBoltAgent()) {
      const reasoning = await this.reason(message.id, userContext);

      message.reasoningData = reasoning;
    }

    const nextMessages = [...messages, { ...message, resolved: true }];

    trackAttemptFix(userContext);
    trackMessageErrors(nextMessages);

    chatHooks.setMessages(nextMessages);

    this.resetMessage();

    await chatHooks.reload();
  }

  ignore() {
    if (this.currentProblems.get().length === 0) {
      return;
    }

    chatHooks.setMessages((messages) => {
      const nextMessages = [...messages, { ...this.getMessage() }];

      trackMessageErrors(nextMessages);

      if (isCurrentChatUsingBoltAgent()) {
        return nextMessages;
      }

      return messages;
    });

    this.resetMessage();
  }

  clear(toClear?: (problem: PotentialProblem) => boolean) {
    if (toClear) {
      const filtered = this.currentProblems.get().filter((problem) => !toClear(problem));
      this.currentProblems.set(filtered);
    } else {
      this.currentProblems.set([]);
    }
  }

  resetMessage() {
    this.id = generateId();
    this.currentProblems.set([]);
  }

  async reason(messageId: string, userContext?: string): Promise<ErrorReasoning> {
    const files = fileMapToImportFiles(workbenchStore.files.get());

    const response = await clientFetch('/api/error/reasoning', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        files,
        errors: renderProblems(this.currentProblems.get(), userContext),
        messageId,
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to reason about error');
    }

    const reasoning = await response.json<ErrorReasoning>();

    if (!reasoning.errorFixes[0]?.solution) {
      throw new Error('Reasoning missing');
    }

    return reasoning;
  }

  // workaround to get state from hook
  async getChatMessages() {
    return await new Promise<Message[]>((resolve) =>
      chatHooks.setMessages((messages) => {
        resolve(messages);

        return messages;
      }),
    );
  }

  // when rendering, appended to the most recent message list if there are problems
  getMessage(userContext?: string): PotentialProblemsMessage {
    const currentProblems = this.currentProblems.get();

    return {
      id: this.id,
      problems: currentProblems,
      content: renderProblems(currentProblems, userContext),
      type: 'problems',
      status: 'unsent',
      resolved: false,
      role: 'user',
      parts: [],
    };
  }
}

export const errorHandler = new ErrorHandler();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { errorHandler });
}
